import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as ShoppingCartActions from './shoppingCart.actions';
import { from, of } from 'rxjs';
import { ShoppingCartService } from '../services/shoppingCart.service';
import { checkoutActions } from '../../checkout/store/checkout.actions';
import { Router } from '@angular/router';
import { ToastService } from '../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ShoppingCartEffects {
  private router = inject(Router);

  constructor(private actions$: Actions,
    private shoppingCartService: ShoppingCartService,
    private toastService: ToastService,
    private translate: TranslateService)
  {}

    addToCartEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.addToCart),
        switchMap((action) =>
          this.shoppingCartService.addToCart(action.item).pipe(
            map(cart => {
              return ShoppingCartActions.addToCartSuccess( cart )
            }),
            catchError(() => {
              return of(ShoppingCartActions.addToCartFailure({ error: 'Error adding item to cart' }));
            })
          )
        )
      )
    );

    addToCartErrorEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.addToCartFailure),
        tap((action) =>  {
          this.toastService.Error(this.translate.instant("shoppingCart.addToCartError"), '')
        }),
        map(() => {
          return { type: '' };
        })
      )
    );

    removeFromCartEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.removeFromCart),
        switchMap((action) =>
          this.shoppingCartService.removeFromCart(action.id).pipe(
            map(cart => ShoppingCartActions.removeFromCartSuccess( cart )),
            catchError(() => of(ShoppingCartActions.removeFromCartFailure({ error: 'Error adding item to cart' })))
          )
        )
      )
    );

    updateCartQuantityEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.updateCartQuantity),
        switchMap((action) =>
          this.shoppingCartService.updateCartQuantity(action.item).pipe(
            map(item => ShoppingCartActions.updateCartQuantitySuccess( item )),
            catchError(() => of(ShoppingCartActions.updateCartQuantityFailure({ error: 'Error updating cart' })))
          )
        )
      )
    );

    checkCartEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.verifyCart),
        switchMap((action) =>
          this.shoppingCartService.checkCart(action.cartId).pipe(
            switchMap(cart => {
              if (cart.enrolled) {
                const courseId = cart.items[0].productId;
                return of(checkoutActions.enrollSuccess(courseId));
              } else {
                return from(this.router.navigate(['/checkout'])).pipe(
                  map(() => checkoutActions.navigationSuccess()),
                  catchError(() => of(checkoutActions.navigationFailure({ error: 'Navigation Failed' })))
                );
              }
            }),
            catchError(() => of(ShoppingCartActions.verifyCheckoutFailure({ error: 'Error adding item to checkout' })))
          )
        )
      )
    );

    enrollSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(checkoutActions.enrollSuccess),
        switchMap(() =>
          from(this.router.navigate(['/course/enrolled'])).pipe(
            map(() => checkoutActions.navigationSuccess()),
            catchError(() => of(checkoutActions.navigationFailure({ error: 'Navigation Failed' })))
          )
        )
      )
    );

    getCartDataEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.getCartData),
        switchMap((action) =>
          this.shoppingCartService.getCartData(action.id).pipe(
            map(cart => ShoppingCartActions.getCartDataSuccess(cart)),
            catchError(() => of(ShoppingCartActions.addToCartFailure({ error: 'Error getting cart data' })))
          )
        )
      )
    );

    getMyCartEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.getMyCart),
        switchMap(() =>
          this.shoppingCartService.getMyCart().pipe(
            map(cart => {
              return ShoppingCartActions.getCartDataSuccess( cart )
            }),
            catchError(() => of(ShoppingCartActions.getCartDataFailure({ error: 'Error getting cart data' })))
          )
        )
      )
    );

    emptyCartEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingCartActions.emptyCart),
        switchMap(() =>
          this.shoppingCartService.emptyCart().pipe(
            map(deleted => {
              if (deleted) {
                return ShoppingCartActions.emptyCartSuccess( deleted )
              } else {
                return ShoppingCartActions.emptyCartFailure( { error: "Error deleting cart data"} );
              }
            }),
            catchError(() => of(ShoppingCartActions.emptyCartFailure({ error: 'Error deleting cart data' })))
          )
        )
      )
    );
}
